import {useLazyQuery} from "@apollo/client";
import {useState} from "react";
import {toast} from "react-hot-toast";

import {DeleteActionDialog} from "@/components/deleteActionDialog/DeleteActionDialog.tsx";
import {DropdownAction, DropdownActionItem} from "@/components/quickActionsBar/DropdownAction.tsx";
import {DropdownDivider} from "@/components/quickActionsBar/DropdownDivider.tsx";
import {QuickActionsBar} from "@/components/quickActionsBar/QuickActionsBar.tsx";
import {EditScheduledReportResponse, GQL_EDIT_SCHEDULED_REPORT} from "@/lib/queries/reports.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {deleteScheduledReport} from "@/pages/reports/schedule/quickActions.ts";
import {ScheduledReportUpdateDrawer} from "@/pages/reports/schedule/ScheduledReportUpdateDrawer.tsx";

interface ScheduledReportActionsProps {
  id: number;
  name: string;
}

export const ScheduledReportActions = ({id, name}: ScheduledReportActionsProps) => {
  const [loadReport, {data, loading}] = useLazyQuery<EditScheduledReportResponse>(GQL_EDIT_SCHEDULED_REPORT, {
    variables: {
      id,
      offset: 0,
      limit: 500,
    },
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
  });
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const dropdownItems = [
    <DropdownActionItem
      text="Edit Report"
      onClick={async () => {
        setIsDuplicate(false);
        await loadReport({variables: {id}});
        setShowEdit(true);
      }}
    />,
    <DropdownDivider />,
    <DropdownActionItem
      text="Duplicate"
      onClick={async () => {
        await loadReport({variables: {id}});
        setIsDuplicate(true);
        setShowEdit(true);
      }}
    />,
    <DropdownActionItem text="Delete" onClick={() => setShowConfirm(true)} />,
  ];
  const actions = [<DropdownAction id={`scheduled-report-dd-${id}`} items={dropdownItems} />];

  return (
    <>
      <DeleteActionDialog
        show={showConfirm}
        title="Delete Scheduled report"
        onCancel={() => setShowConfirm(false)}
        onConfirm={async () => {
          const {data} = await deleteScheduledReport(id);
          if (data?.response.status === ResponseStatus.SUCCESS) {
            toast.success(`${name} report has been deleted`);
          }
          setShowConfirm(false);
        }}
      >
        Are you sure you want to delete <code>{name}</code> report?
      </DeleteActionDialog>

      {!loading && data && (
        <ScheduledReportUpdateDrawer
          title={(isDuplicate ? "Duplicate" : "Edit") + " Scheduled Report"}
          show={showEdit}
          onClose={() => {
            setShowEdit(false);
          }}
          defaultValues={isDuplicate ? {...data.scheduledReport, id: undefined} : data.scheduledReport}
          slaReports={data.slaReports.items}
          users={data.currentAccountUsers}
        />
      )}
      <QuickActionsBar id={`scheduled-report-act-${id}`} actions={actions} />
    </>
  );
};
